<template>
  <div ref="container" class="truncate-container" :title="props.text">
    {{ displayText }}
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";

const props = defineProps({
  text: { type: String, required: true },
  maxChars: { type: Number, default: 30 },
});

function truncateMiddle(str, maxLength = 30) {
  if (!str || str.length <= maxLength) return str;
  const keep = Math.floor(maxLength / 2) - 1;
  return str.substring(0, keep) + "..." + str.substring(str.length - keep);
}

const container = ref(null);
const maxChars = ref(props.maxChars);
const displayText = ref(props.text);

// Measure text width to calculate characters per pixel
let charWidth = 0;
let isMeasuring = false;

const measureTextWidth = () => {
  if (!container.value || isMeasuring) return;

  isMeasuring = true;

  // Create a temporary span to measure character width
  const tempSpan = document.createElement("span");
  tempSpan.style.position = "absolute";
  tempSpan.style.visibility = "hidden";
  tempSpan.style.whiteSpace = "nowrap";
  tempSpan.style.font = window.getComputedStyle(container.value).font;
  tempSpan.textContent = "M".repeat(10); // Use 10 characters to get average width
  document.body.appendChild(tempSpan);

  const measuredWidth = tempSpan.offsetWidth;
  charWidth = measuredWidth / 10;

  document.body.removeChild(tempSpan);
  isMeasuring = false;
};

const checkTextFits = (text) => {
  if (!container.value || !charWidth) return false;

  const tempSpan = document.createElement("span");
  tempSpan.style.position = "absolute";
  tempSpan.style.visibility = "hidden";
  tempSpan.style.whiteSpace = "nowrap";
  tempSpan.style.font = window.getComputedStyle(container.value).font;
  tempSpan.textContent = text;
  document.body.appendChild(tempSpan);

  const textWidth = tempSpan.offsetWidth;
  const containerWidth = container.value.offsetWidth;

  document.body.removeChild(tempSpan);

  return textWidth <= containerWidth;
};

const updateDisplay = () => {
  if (!container.value || !charWidth) return;

  // First, check if the full text fits
  if (checkTextFits(props.text)) {
    displayText.value = props.text;
    return;
  }

  // If full text doesn't fit, calculate truncation
  const containerWidth = container.value.offsetWidth;
  const availableChars = Math.floor(containerWidth / charWidth);

  // Ensure we don't go below a minimum (at least 5 chars + "...")
  const minChars = 8;
  const newMaxChars = Math.max(availableChars, minChars);

  // Only update if there's a significant change to prevent micro-adjustments
  if (Math.abs(newMaxChars - maxChars.value) > 2) {
    maxChars.value = newMaxChars;
  }

  // Apply truncation
  displayText.value = truncateMiddle(props.text, maxChars.value);
};

let resizeTimeout = null;

const resizeObserver = new ResizeObserver(() => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    updateDisplay();
  }, 50);
});

onMounted(async () => {
  await nextTick();
  measureTextWidth();
  updateDisplay();

  if (container.value) {
    resizeObserver.observe(container.value);
  }
});

onUnmounted(() => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeObserver.disconnect();
});
</script>

<style scoped>
.truncate-container {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
}
</style>
